import { useCallback } from 'react';
import axios from 'axios';
import { useSyncExternalStore } from 'use-sync-external-store/shim';
import { isEqual } from 'lodash';

type PollingState<T> = {
  data: T | null;
  isLoading: boolean;
};

type PollingStore<T> = {
  state: PollingState<T>;
  listeners: Set<() => void>;
  intervalId: ReturnType<typeof setInterval> | null;
};

const stores = new Map<string, PollingStore<unknown>>();

const getOrCreateStore = <T>(endpoint: string): PollingStore<T> => {
  if (!stores.has(endpoint)) {
    stores.set(endpoint, {
      state: { data: null, isLoading: true },
      listeners: new Set(),
      intervalId: null,
    });
  }
  return stores.get(endpoint) as PollingStore<T>;
};

type usePolledDataProp = {
  endpoint: string;
  intervalMs?: number;
  enable?: boolean;
};

export const usePolledData = <T>({
  endpoint,
  intervalMs = 1000,
  enable = true,
}: usePolledDataProp): PollingState<T> => {
  const store = getOrCreateStore<T>(endpoint);

  const subscribe = useCallback(
    (callback: () => void) => {
      if (!enable) {
        return () => {};
      }

      store.listeners.add(callback);

      if (store.listeners.size === 1) {
        const fetch = async () => {
          try {
            const res = await axios.get<T>(endpoint);
            const newData = res.data;
            if (!isEqual(newData, store.state.data)) {
              store.state = { data: newData, isLoading: false };
              store.listeners.forEach((callbackFn) => callbackFn());
            }
          } catch {
            const hasChanged = store.state.data !== null || store.state.isLoading;
            if (hasChanged) {
              store.state = { data: null, isLoading: false };
              store.listeners.forEach((callbackFn) => callbackFn());
            }
          }
        };

        fetch();
        store.intervalId = setInterval(fetch, intervalMs);
      }

      return () => {
        store.listeners.delete(callback);
        if (store.listeners.size === 0 && store.intervalId) {
          clearInterval(store.intervalId);
          store.intervalId = null;
        }
      };
    },
    [endpoint, intervalMs, store, enable],
  );

  const getSnapshot = useCallback(() => store.state, [store]);
  const getServerSnapshot = () => ({ data: null, isLoading: true });

  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
};
